import React, { useEffect, useState, useRef, ReactNode } from 'react';
import { BrowserSessionContext } from './BrowserSessionContext';

const storageKeyBrowserUnifiedSessionId = 'skyGo.browserUnifiedSessionId';
const storageKeyBrowserActiveSessions = 'skyGo.browserActiveSessions';
const broadcastChannelId = 'session_channel';

interface BrowserSessionProviderProps {
  children: ReactNode;
}
interface BrowserSessionBroadcastMessage {
  type: 'session-update';
  unifiedSessionId: string | null;
}

export const generateAlphanumericString = (length = 8): string => {
  const characters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
  const array = new Uint8Array(length);
  window.crypto.getRandomValues(array);
  return Array.from(array, byte => characters[byte % characters.length]).join('');
};

export const generateUnifiedSessionId = (): void => {
  if (!localStorage.getItem(storageKeyBrowserUnifiedSessionId)) {
    localStorage.setItem(storageKeyBrowserUnifiedSessionId, generateAlphanumericString());
  }
};

export const getUnifiedSessionId = (): string | null =>
  localStorage.getItem(storageKeyBrowserUnifiedSessionId) || null;

export const getBrowserSessions = (): string[] => {
  try {
    return JSON.parse(localStorage.getItem(storageKeyBrowserActiveSessions) || '[]') as string[];
  } catch {
    return [];
  }
};

const BrowserSessionProvider: React.FC<BrowserSessionProviderProps> = ({ children }) => {
  const [unifiedSessionId, setUnifiedSessionId] = useState<string | null>(null);

  const browserIdRef = useRef<string>(generateAlphanumericString(16));
  const broadcastChannelRef = useRef<BroadcastChannel | null>(null);

  useEffect(() => {
    if (typeof BroadcastChannel !== 'undefined') {
      broadcastChannelRef.current = new BroadcastChannel(broadcastChannelId);
    }

    // 1. Register this tab/window
    const currentBrowserSessions = getBrowserSessions();
    const updatedBrowserSessions = Array.from(
      new Set([...currentBrowserSessions, browserIdRef.current]),
    );
    localStorage.setItem(storageKeyBrowserActiveSessions, JSON.stringify(updatedBrowserSessions));

    // 2. Get existing unifiedSessionId (already initialized at app startup)
    setUnifiedSessionId(getUnifiedSessionId());

    // 3. BroadcastChannel listener
    const messageHandler = (event: MessageEvent<BrowserSessionBroadcastMessage>) => {
      if (event.data.type === 'session-update') {
        setUnifiedSessionId(event.data.unifiedSessionId);
      }
    };
    broadcastChannelRef.current?.addEventListener('message', messageHandler);

    // 4. Handle closed window browsers
    const cleanUp = () => {
      const activeBrowserSessions = getBrowserSessions();
      const remaining = activeBrowserSessions.filter(browser => browser !== browserIdRef.current);
      try {
        localStorage.setItem(storageKeyBrowserActiveSessions, JSON.stringify(remaining));
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Failed to update active browser sessions:', error);
      }

      // Clear unifiedSessionId on last tab/window close
      if (remaining.length === 0) {
        localStorage.removeItem(storageKeyBrowserUnifiedSessionId);
        broadcastChannelRef.current?.postMessage({
          type: 'session-update',
          unifiedSessionId: null,
        });
      }

      broadcastChannelRef.current?.removeEventListener('message', messageHandler);
    };

    window.addEventListener('beforeunload', cleanUp);

    // 5. localStorage listener (other opened tab/windows)
    const storageListener = (evt: StorageEvent) => {
      if (evt.key === storageKeyBrowserUnifiedSessionId) {
        setUnifiedSessionId(evt.newValue);
      }
    };

    window.addEventListener('storage', storageListener);

    return () => {
      window.removeEventListener('beforeunload', cleanUp);
      window.removeEventListener('storage', storageListener);
      cleanUp();
      broadcastChannelRef.current?.close();
    };
  }, []);

  return (
    <BrowserSessionContext.Provider value={{ unifiedSessionId }}>
      {children}
    </BrowserSessionContext.Provider>
  );
};

export default BrowserSessionProvider;
